import React, { useEffect, useMemo, useRef, useState } from 'react';

import { Box } from '@atlaskit/primitives/compiled';

import type { PrimaryProductKey } from '../../../../../common/constants/product-keys';
import { useAppContext } from '../../../../../common/ui/app-context';
import LeapQueryComponent from '../../../../../common/ui/leap-query';
import { onLeapQueryCandidateClicked } from '../../../../../common/utils/events/leap-query-candidate-clicked';
import { onLeapQueryShown } from '../../../../../common/utils/events/leap-query-shown';
import { getLeapQueryMemo } from '../../../../../common/utils/get-leap-query-memo';
import { useAnalytics, useCommonAndPrivateAttributes } from '../../../../../controllers/analytics';
import { ErrorBoundary } from '../../../../../controllers/error-boundary';

export type LeapQueryProps = {
	searchQuery: string | undefined;
	originalQuery: string | null | undefined;
	primaryProduct: PrimaryProductKey | undefined;
	logError: (error: any) => Promise<void>;
};

type LeapQueryState = {
	candidates: string[];
	titleIndex: number;
	loading: boolean;
};

const LeapQuery = ({ searchQuery, originalQuery, primaryProduct, logError }: LeapQueryProps) => {
	const { fireAnalyticsEvent } = useAnalytics();
	const { commonAttributes, nonPrivacySafeAttributes } = useCommonAndPrivateAttributes();
	const [{ candidates, titleIndex, loading }, setLeapQuery] = useState<LeapQueryState>(() => {
		const response =
			searchQuery && primaryProduct
				? getLeapQueryMemo({ query: searchQuery, product: primaryProduct }).getValue()
				: undefined;
		return {
			candidates: response?.candidates || [],
			titleIndex: response?.title || 0,
			loading: searchQuery && primaryProduct ? !response : false,
		};
	});
	const { generateSearchUrl, queryParams } = useAppContext();
	const hasCandidates = candidates.length > 0;
	const analyticsDataRef = useRef({
		fireAnalyticsEvent,
		commonAttributes,
		nonPrivacySafeAttributes,
		titleIndex,
	});
	// Hack to ensure that the analytics data is not stale
	analyticsDataRef.current = {
		fireAnalyticsEvent,
		commonAttributes,
		nonPrivacySafeAttributes,
		titleIndex,
	};

	useEffect(() => {
		if (!searchQuery || !primaryProduct || hasCandidates) {
			return;
		}
		setLeapQuery((prevState) => ({ ...prevState, loading: true }));
		getLeapQueryMemo({
			query: searchQuery,
			product: primaryProduct,
		})
			.then((response) => {
				setLeapQuery({
					candidates: response.candidates,
					titleIndex: response.title,
					loading: false,
				});
			})
			.catch((e) => {
				setLeapQuery({
					candidates: [],
					titleIndex: 0,
					loading: false,
				});
				logError(e);
			});
	}, [hasCandidates, searchQuery, primaryProduct, setLeapQuery, logError]);

	const mappedCandidates = useMemo(
		() =>
			candidates.slice(0, 4).map((candidate) => ({
				label: candidate,
				href: generateSearchUrl({
					...queryParams,
					text: candidate,
					originalQuery,
				}),
			})),
		[candidates, generateSearchUrl, originalQuery, queryParams],
	);

	const handleCandidateClicked = (candidateIndex: number) => {
		fireAnalyticsEvent(
			onLeapQueryCandidateClicked({
				candidateIndex,
				titleIndex,
				commonAttributes,
				nonPrivacySafeAttributes,
			}),
		);
	};

	useEffect(() => {
		if (loading || mappedCandidates.length === 0) {
			return;
		}

		const { fireAnalyticsEvent, titleIndex, commonAttributes, nonPrivacySafeAttributes } =
			analyticsDataRef.current;

		fireAnalyticsEvent(
			onLeapQueryShown({
				candidatesCount: mappedCandidates.length,
				titleIndex,
				commonAttributes,
				nonPrivacySafeAttributes,
			}),
		);
	}, [mappedCandidates.length, loading, analyticsDataRef]);

	if (!loading && mappedCandidates.length === 0) {
		return null;
	}

	return (
		<Box role="listitem">
			<LeapQueryComponent
				candidates={mappedCandidates}
				loading={loading}
				titleIndex={titleIndex}
				handleCandidateClicked={handleCandidateClicked}
			/>
		</Box>
	);
};

export const LeapQueryBanner = (props: LeapQueryProps): React.JSX.Element => (
	<ErrorBoundary section="SearchPageLeapQuery">
		<LeapQuery {...props} />
	</ErrorBoundary>
);
